import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { EXTERIOR_MAX_REGULAR } from "@/config/constants";
import {
  ENGINE_VIDEO_ACCEPT,
  ENGINE_VIDEO_COMPRESS_URL,
  ENGINE_VIDEO_CONVERT_URL,
  ENGINE_VIDEO_MAX_MB,
  ENGINE_VIDEO_MAX_SECONDS,
} from "@/lib/engineVideo";
import { X, RotateCcw, RotateCw, Video, Play, Loader2, Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ImageUploadButton } from "../ImageUploadButton";
import { SortableImageGrid } from "../SortableImageGrid";
import type { VehicleTestImages, ExteriorImage } from "@/types/vehicleTest";
import {
  applyExteriorThumbnailFlags,
  sortExteriorImages,
} from "./exteriorImageHelpers";
import { photoSessionSourceProps } from "../../photoSession/photoSessionButtonProps";
import { isVideoMedia } from "@/lib/isVideoMedia";
import { cn } from "@/lib/utils";
import { isIOS, isAndroid } from "@/utils/platformUtils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useIsMobile } from "@/hooks/use-mobile";

interface ExteriorSectionProps {
  exteriorImages: ExteriorImage[];
  uploadingExterior: boolean;
  /** True while motor video is uploading — spinner on video area, not photo slot. */
  uploadingEngineVideo?: boolean;
  onImageClick: (image: unknown, alt: string) => void;
  getThumbnailUrl?: (image: unknown) => string;
  onRemoveImage: (
    section: keyof VehicleTestImages,
    index: number,
    subSection?: string,
    imageIdForExterior?: string,
  ) => void;
  onRotate?: (image: unknown, direction: "left" | "right") => void;
  onFileSelect: (files: File[]) => Promise<void>;
  onSelect: () => void;
  onCameraSelect: () => void;
  /** Gallery/file upload of a short engine video (validated in processImageFile). */
  onVideoSelect?: (file: File) => void;
  /** Mobile: open guided photo session (engine / video step). */
  onEngineVideoCameraSelect?: () => void;
  onReorder?: (newOrder: ExteriorImage[]) => void;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
}

function isEngineVideoImage(img: ExteriorImage): boolean {
  return isVideoMedia(img.type, img.url);
}

export const ExteriorSection: React.FC<ExteriorSectionProps> = ({
  exteriorImages: exteriorImagesProp,
  uploadingExterior,
  uploadingEngineVideo = false,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onRotate,
  onFileSelect,
  onSelect,
  onCameraSelect,
  onVideoSelect,
  onEngineVideoCameraSelect,
  onReorder,
  rotatingImageId,
  getImageRotation,
}) => {
  const { language } = useLanguage();
  const lp = listingPostLabels(language);
  const ev = ((lp.images as { engineVideo?: Record<string, string> })
    .engineVideo ?? {}) as Record<string, string>;
  const videoInputRef = useRef<HTMLInputElement>(null);
  const infoRootRef = useRef<HTMLDivElement>(null);
  const infoPanelId = useId();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoPinned, setInfoPinned] = useState(false);
  const [infoPos, setInfoPos] = useState<{ top: number; right: number } | null>(
    null,
  );
  const [engineSourceOpen, setEngineSourceOpen] = useState(false);
  const { isNative } = useNativePlatform();
  const isMobile = useIsMobile();
  const showEngineSourcePicker =
    Boolean(onEngineVideoCameraSelect) && (isIOS() || isAndroid() || isNative);
  /** Hover only on real desktop — touch devices fire phantom mouseenter/leave. */
  const canHover =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const exteriorImages = sortExteriorImages(exteriorImagesProp || []);

  useLayoutEffect(() => {
    if (isMobile || !infoOpen || !infoRootRef.current) {
      setInfoPos(null);
      return;
    }
    const rect = infoRootRef.current.getBoundingClientRect();
    setInfoPos({
      top: rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, [infoOpen, isMobile]);

  useEffect(() => {
    if (isMobile || !infoOpen) return;
    let onPointerDown: ((e: PointerEvent) => void) | null = null;
    // Defer so the opening click/tap does not immediately dismiss the panel.
    const timer = window.setTimeout(() => {
      onPointerDown = (e: PointerEvent) => {
        const target = e.target as Node;
        if (
          infoRootRef.current?.contains(target) ||
          (target instanceof Element &&
            target.closest(`[data-engine-info-panel="${infoPanelId}"]`))
        ) {
          return;
        }
        setInfoPinned(false);
        setInfoOpen(false);
      };
      document.addEventListener("pointerdown", onPointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      if (onPointerDown) {
        document.removeEventListener("pointerdown", onPointerDown, true);
      }
    };
  }, [infoOpen, infoPanelId, isMobile]);

  const { photoImages, engineVideo } = useMemo(() => {
    const engine = exteriorImages.find((img) => isEngineVideoImage(img)) ?? null;
    const photos = exteriorImages.filter((img) => !isEngineVideoImage(img));
    return { photoImages: photos, engineVideo: engine };
  }, [exteriorImages]);

  // Count photos toward max; engine video is separate
  const canAddMore = photoImages.length < EXTERIOR_MAX_REGULAR;

  const removeByImage = (img: ExteriorImage | null | undefined) => {
    if (!img) return;
    const actualIndex = exteriorImagesProp.findIndex((e) => e.id === img.id);
    if (actualIndex !== -1) {
      onRemoveImage("exterior", actualIndex, undefined, img.id);
    }
  };

  const handleRemovePhoto = (gridIndex: number) => {
    removeByImage(photoImages[gridIndex]);
  };

  const mergePhotosWithEngine = (photos: ExteriorImage[]) => {
    const engines = exteriorImagesProp.filter((img) => isEngineVideoImage(img));
    return applyExteriorThumbnailFlags([...photos, ...engines]);
  };

  const uploadButton = canAddMore ? (
    <ImageUploadButton
      onSelect={onSelect}
      onFileSelect={onFileSelect}
      onCameraSelect={onCameraSelect}
      label={
        <>
          {lp.images.uploadLabel}{" "}
          <span className="font-semibold">{lp.images.maxPerBatch5}</span>
        </>
      }
      isThumbnail={false}
      disabled={false}
      isLoading={uploadingExterior}
      section="exterior"
      {...photoSessionSourceProps(lp.images)}
    />
  ) : null;

  const engineVideoThumbSrc = engineVideo
    ? getThumbnailUrl
      ? getThumbnailUrl(engineVideo)
      : engineVideo.url
    : "";
  const engineVideoSrc =
    engineThumbSrcSafe(engineVideoThumbSrc);

  const infoLines = [
    ev.checklistIdle,
    ev.checklistFilm,
    (ev.checklistLimit ?? "")
      .replace("{seconds}", String(ENGINE_VIDEO_MAX_SECONDS))
      .replace("{mb}", String(ENGINE_VIDEO_MAX_MB)),
    ev.checklistSafari,
  ].filter(Boolean) as string[];

  const engineInfoBody = (
    <div className="space-y-2 text-sm">
      <ul className="list-disc space-y-1.5 pl-4 text-muted-foreground">
        {infoLines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {ev.checklistConvert ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          {ev.checklistConvert}{" "}
          <a
            href={ENGINE_VIDEO_CONVERT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-foreground font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {ev.convertLink ?? "WebM → MP4"}
          </a>
        </p>
      ) : null}
      <a
        href={ENGINE_VIDEO_COMPRESS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block underline text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        {ev.compressLink}
      </a>
    </div>
  );

  /** Mobile: bottom sheet. Desktop: portaled bubble above Dialog (z-220+). */
  const engineInfoButton = (
    <div
      ref={infoRootRef}
      className="absolute top-1 right-1 z-20"
      onMouseEnter={() => {
        if (!isMobile && canHover) setInfoOpen(true);
      }}
      onMouseLeave={() => {
        if (!isMobile && canHover && !infoPinned) setInfoOpen(false);
      }}
    >
      <button
        type="button"
        className="rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 touch-manipulation"
        aria-label={ev.infoAria ?? "Info om motorvideo"}
        aria-expanded={infoOpen}
        aria-controls={infoPanelId}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isMobile) {
            setInfoOpen(true);
            return;
          }
          if (infoOpen && infoPinned) {
            setInfoPinned(false);
            setInfoOpen(false);
          } else {
            setInfoPinned(true);
            setInfoOpen(true);
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Info className="h-4 w-4" />
      </button>
      {isMobile ? (
        <Sheet open={infoOpen} onOpenChange={setInfoOpen}>
          <SheetContent
            side="bottom"
            nestedSheet
            overlayClassName="!z-[280]"
            className="!z-[281] rounded-t-2xl max-h-[75dvh]"
          >
            <SheetHeader>
              <SheetTitle className="text-center">
                {ev.title ?? "Motorvideo"}
              </SheetTitle>
            </SheetHeader>
            <div className="py-4 overflow-y-auto">{engineInfoBody}</div>
          </SheetContent>
        </Sheet>
      ) : infoOpen && infoPos ? (
        createPortal(
          <div
            id={infoPanelId}
            data-engine-info-panel={infoPanelId}
            role="tooltip"
            className="fixed w-[16rem] max-w-[calc(100vw-1rem)] rounded-md border bg-popover p-2 text-xs text-popover-foreground shadow-lg space-y-1 z-[280]"
            style={{ top: infoPos.top, right: infoPos.right }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (canHover) setInfoOpen(true);
            }}
            onMouseLeave={() => {
              if (canHover && !infoPinned) setInfoOpen(false);
            }}
          >
            <p className="font-semibold">{ev.title ?? "Motorvideo"}</p>
            <ul className="list-disc space-y-0.5 pl-3.5 text-muted-foreground">
              {infoLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {ev.checklistConvert ? (
              <p className="text-muted-foreground leading-relaxed">
                {ev.checklistConvert}{" "}
                <a
                  href={ENGINE_VIDEO_CONVERT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-foreground font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {ev.convertLink ?? "WebM → MP4"}
                </a>
              </p>
            ) : null}
            <a
              href={ENGINE_VIDEO_COMPRESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block underline"
              onClick={(e) => e.stopPropagation()}
            >
              {ev.compressLink}
            </a>
          </div>,
          document.body,
        )
      ) : null}
    </div>
  );

  /** Same footprint as photo upload tile — last in the grid. */
  const engineVideoSlot =
    onVideoSelect ? (
      <div className="relative">
        <input
          ref={videoInputRef}
          type="file"
          accept={ENGINE_VIDEO_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onVideoSelect(file);
          }}
        />
        {engineVideo ? (
          <div className="relative group">
            <button
              type="button"
              onClick={() =>
                onImageClick(engineVideo, ev.title ?? "Motorvideo")
              }
              className={cn(
                "relative w-full h-24 overflow-hidden rounded border-2 border-dashed border-gray-300 transition-colors",
                "hover:border-gray-400",
              )}
              aria-label={ev.title ?? "Motorvideo"}
              title={ev.title ?? "Motorvideo"}
            >
              <video
                src={engineVideoSrc}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 h-full w-full object-cover pointer-events-none"
              />
              <span className="absolute inset-0 bg-black/45" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-white">
                <Play
                  className="h-5 w-5 fill-white drop-shadow-md translate-x-px"
                  strokeWidth={0}
                />
                <span className="text-[10px] font-medium tracking-[0.14em] uppercase text-white/95">
                  {ev.shortLabel ?? "Motor"}
                </span>
              </span>
            </button>
            {engineInfoButton}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeByImage(engineVideo);
              }}
              className="absolute -top-1 -left-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
              aria-label={ev.remove ?? "Ta bort motorvideo"}
              title={ev.remove ?? "Ta bort motorvideo"}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            {engineInfoButton}
            {showEngineSourcePicker ? (
              <div className="relative w-full h-24">
                {engineSourceOpen ? (
                  <div className="w-full h-24 border-2 border-dashed rounded flex flex-col items-center justify-center border-gray-300">
                    <Select
                      onValueChange={(value) => {
                        setTimeout(() => {
                          if (value === "gallery") {
                            videoInputRef.current?.click();
                          } else if (value === "camera") {
                            onEngineVideoCameraSelect?.();
                          }
                          setEngineSourceOpen(false);
                        }, 100);
                      }}
                      onOpenChange={setEngineSourceOpen}
                    >
                      <SelectTrigger className="w-full h-full border-0 shadow-none focus:ring-0 flex items-center justify-center">
                        <span className="text-xs text-muted-foreground">
                          {ev.chooseSource ?? "Välj…"}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="camera">
                          {ev.takeVideo ?? "Ta video"}
                        </SelectItem>
                        <SelectItem value="gallery">
                          {ev.chooseGallery ?? "Välj från galleri"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={uploadingEngineVideo || uploadingExterior}
                    onClick={() => setEngineSourceOpen(true)}
                    className={cn(
                      "relative w-full h-24 border-2 border-dashed rounded flex flex-col items-center justify-center transition-colors",
                      "border-gray-300 hover:border-gray-400",
                      (uploadingEngineVideo || uploadingExterior) &&
                        "opacity-50 cursor-not-allowed",
                    )}
                    aria-label={ev.button ?? "Lägg till motorvideo"}
                  >
                    {uploadingEngineVideo ? (
                      <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <span className="absolute top-1 left-1 inline-flex h-[14px] items-center justify-center rounded-full bg-red-600 px-1 text-[7px] font-bold uppercase leading-none tracking-wide text-white">
                          {ev.newBadge ?? "Nytt"}
                        </span>
                        <Video className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1 text-center px-1">
                          {ev.shortLabel ?? "Motor"}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                disabled={uploadingEngineVideo || uploadingExterior}
                onClick={() => videoInputRef.current?.click()}
                className={cn(
                  "relative w-full h-24 border-2 border-dashed rounded flex flex-col items-center justify-center transition-colors",
                  "border-gray-300 hover:border-gray-400",
                  (uploadingEngineVideo || uploadingExterior) &&
                    "opacity-50 cursor-not-allowed",
                )}
                aria-label={ev.button ?? "Lägg till motorvideo"}
              >
                {uploadingEngineVideo ? (
                  <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                ) : (
                  <>
                    <span className="absolute top-1 left-1 inline-flex h-[14px] items-center justify-center rounded-full bg-red-600 px-1 text-[7px] font-bold uppercase leading-none tracking-wide text-white">
                      {ev.newBadge ?? "Nytt"}
                    </span>
                    <Video className="h-6 w-6 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1 text-center px-1">
                      {ev.shortLabel ?? "Motor"}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    ) : null;

  const gridExtras = (
    <>
      {uploadButton}
      {engineVideoSlot}
    </>
  );

  return (
    <Card className="border-0">
      <CardContent className="pt-2">
        <p className="text-sm text-muted-foreground mb-3">
          {lp.images.exteriorThumbnailHint}
        </p>

        {onReorder ? (
          <SortableImageGrid
            images={photoImages}
            section="exterior"
            onImageClick={onImageClick}
            getThumbnailUrl={getThumbnailUrl}
            onRemoveImage={handleRemovePhoto}
            onRotate={onRotate}
            onReorder={(newOrder) =>
              onReorder(mergePhotosWithEngine(newOrder as ExteriorImage[]))
            }
            rotatingImageId={rotatingImageId}
            getImageRotation={getImageRotation}
            useIndexZeroAsThumbnail
          >
            {gridExtras}
          </SortableImageGrid>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {photoImages.map((img, index) => {
              const rot = getImageRotation?.(img.id ?? "") ?? 0;
              return (
                <div key={img.id || index} className="relative">
                  <div className="relative group">
                    <div
                      className="w-full h-full"
                      style={
                        rot ? { transform: `rotate(${rot}deg)` } : undefined
                      }
                    >
                      {(() => {
                        const thumbSrc = getThumbnailUrl
                          ? getThumbnailUrl(img)
                          : img.url;
                        return (
                          <img
                            src={thumbSrc}
                            alt={`${lp.images.exterior} ${index + 1}`}
                            className={`w-full h-24 object-cover rounded border cursor-pointer ${
                              index === 0 ? "border-blue-500 border-2" : ""
                            }`}
                            onClick={() =>
                              onImageClick(
                                img,
                                `${lp.images.exterior} ${index + 1}`,
                              )
                            }
                          />
                        );
                      })()}
                    </div>
                    <Badge
                      variant="secondary"
                      className="absolute top-1 left-1 h-6 min-w-6 flex items-center justify-center rounded-full bg-black/70 text-white text-xs font-semibold"
                    >
                      {index + 1}
                    </Badge>
                    {index === 0 && (
                      <Badge
                        variant="secondary"
                        className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs"
                      >
                        {lp.images.mainImage}
                      </Badge>
                    )}
                    {onRotate && (
                      <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRotate(img, "left");
                          }}
                          className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                          aria-label="Rotera vänster"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRotate(img, "right");
                          }}
                          className="p-1 rounded bg-black/60 hover:bg-black/80 text-white"
                          aria-label="Rotera höger"
                        >
                          <RotateCw className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePhoto(index);
                      }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}
            {gridExtras}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function engineThumbSrcSafe(src: string): string {
  if (!src) return "";
  return src.includes("#") ? src : `${src}#t=0.001`;
}
